import type {
  GetObservationDatasetsOptions,
  GetObservationOptions,
  ObservationDetail,
  ObservationDetailMeta,
  ObservationEntity,
} from "./observation.type";
import type { Comment } from "@/entities/comment";
import type { Locale } from "@/entities/language";

import { useTranslation } from "react-i18next";

import { useGetAssetType } from "@/entities/asset-type";
import { sortDatasetsByDate } from "@/entities/dataset";
import { OBSERVATION_INCLUDES } from "@/entities/observation";
import { useReferentialMaterials } from "@/entities/referential/referential-materials";
import { useGetRiskMatrix } from "@/entities/risk-matrix";
import { useGet, useInfiniteList } from "@/hooks/queries";
import { isValidId } from "@/utilities/id";

import { OBSERVATIONS_MODEL_KEYS } from "./observation.constant";

export const useGetObservation = (
  observationId?: number,
  options?: GetObservationOptions
) => {
  const { data, meta, ...queryResult } = useGet<
    ObservationDetail,
    ObservationDetailMeta
  >("observations", observationId, options);

  return {
    observation: data,
    context: meta?.context,
    ...queryResult,
  };
};

export const useGetObservationWithDatasets = (
  observationId?: number,
  options?: GetObservationDatasetsOptions
) => {
  const { observation, ...queryResult } = useGetObservation(observationId, {
    ...options,
    params: { include: OBSERVATION_INCLUDES.DATASETS },
  });

  const datasets = sortDatasetsByDate({
    datasets: observation?.datasets,
    property: "updated_at",
    order: "desc",
  });

  return {
    ...queryResult,
    observation,
    datasets,
  };
};

export const useGetObservationRiskMatrix = (observation: ObservationEntity) => {
  const riskMatrixId = observation.risk_matrix_id;
  const riskMatrixVersion = observation.risk_matrix_version;

  const { consequence, probability } = observation.source ?? {};
  const hasAtLeastOneRiskMatrixField =
    typeof consequence === "number" || typeof probability === "number";

  const { riskMatrix, ...queryResult } = useGetRiskMatrix(
    riskMatrixId,
    riskMatrixVersion,
    { enabled: hasAtLeastOneRiskMatrixField }
  );

  const { consequences, probabilities, risks } = riskMatrix ?? {};

  const risk = risks?.find(
    (r) => r.consequence === consequence && r.probability === probability
  );

  const consequenceObj = consequences?.find((c) => c.value === consequence);
  const probabilityObj = probabilities?.find((p) => p.value === probability);

  return {
    ...queryResult,
    riskMatrix,
    risk,
    consequence: consequenceObj ?? consequence,
    probability: probabilityObj ?? probability,
  };
};

export const useGetObservationMaterialInfos = (
  observation: ObservationEntity
) => {
  const { material, defect } = observation.source ?? {};

  const enabled = !!material;
  const { materials, ...result } = useReferentialMaterials({ enabled });

  const materialValue = materials.find((m) => m.slug === material);
  const defectValue = materialValue?.defects?.find((d) => d.slug === defect);

  return {
    ...result,
    material: materialValue ?? material,
    defect: defectValue ?? defect,
  };
};

export const useGetObservationComponent = (observation: ObservationEntity) => {
  const { i18n } = useTranslation();
  const language = i18n.language as Locale;

  const assetTypeId = observation.asset?.asset_type?.id;
  const componentSlug = observation.source?.component;
  const enabled = !!(componentSlug && assetTypeId);

  const { assetType, ...result } = useGetAssetType(assetTypeId, { enabled });

  const componentValue =
    assetType?.component_tree_translation?.[language]?.[`${componentSlug}`];

  return {
    ...result,
    component: componentValue ?? componentSlug,
  };
};

export const useGetObservationComments = (observation?: ObservationEntity) => {
  const observationId = observation?.id;

  const { data, hasNextPage, fetchNextPage, ...queryResult } =
    useInfiniteList<Comment>({
      endpoint: OBSERVATIONS_MODEL_KEYS.COMMENTS_LIST,
      enabled: isValidId(observationId),
      rootId: observationId,
    });

  const onLoadMore = () => {
    if (hasNextPage) fetchNextPage();
  };

  return {
    comments: data ?? [],
    hasNextPage,
    onLoadMore,
    ...queryResult,
  };
};
